// =============================================================================
// MeuDriver — InstructorPro
// Landing page da assinatura Pro do instrutor.
// Rota: /instrutor/pro
// =============================================================================

import { useSearchParams } from 'react-router-dom'
import { Star, Search, BarChart3, CalendarCheck, Loader2, CheckCircle } from 'lucide-react'
import { useEffect } from 'react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { InstructorProCard } from '@/components/instructor/InstructorProCard'
import { useInstructorSubscription, useCreateInstructorSubscription } from '@/hooks/useInstructorSubscription'

const BENEFITS = [
  {
    icon: Search,
    title: 'Apareça primeiro nas buscas',
    description: 'Instrutores Pro são exibidos no topo da lista para todos os alunos.',
  },
  {
    icon: Star,
    title: 'Badge "Pro" no perfil',
    description: 'Destaque que transmite confiança e profissionalismo para os alunos.',
  },
  {
    icon: CalendarCheck,
    title: 'Slots ilimitados na agenda',
    description: 'Sem limite de horários disponíveis — ofereça mais flexibilidade.',
  },
  {
    icon: BarChart3,
    title: 'Relatórios avançados de receita',
    description: 'Acompanhe seus ganhos, aulas e tendências em detalhes.',
  },
]

export default function InstructorPro() {
  const [searchParams] = useSearchParams()
  const { data: subscription, isLoading: subLoading } = useInstructorSubscription()
  const createSubscription = useCreateInstructorSubscription()

  useEffect(() => {
    const status = searchParams.get('status')
    if (status === 'success') {
      toast.success('Assinatura Pro iniciada! Aguardando confirmação do pagamento.')
    }
  }, [searchParams])

  const isActive = subscription?.status === 'active'

  return (
    <div className="max-w-lg mx-auto px-4 py-8 space-y-8">
      {/* Header */}
      <div className="text-center space-y-3">
        <div className="inline-flex items-center justify-center h-16 w-16 rounded-full bg-brand-100 mx-auto">
          <Star className="h-8 w-8 text-brand-600 fill-brand-500" />
        </div>
        <h1 className="text-2xl font-bold text-gray-900">MeuDriver Pro</h1>
        <p className="text-muted-foreground text-sm leading-relaxed">
          Expanda sua visibilidade e ganhe mais alunos por apenas
          <span className="font-semibold text-gray-900"> R$49,90/mês</span>.
        </p>
      </div>

      {/* Status banner (se já assinou) */}
      {!subLoading && isActive && (
        <div className="flex items-center gap-2 text-sm text-green-700 font-medium bg-green-50 border border-green-100 rounded-lg px-4 py-3">
          <CheckCircle className="h-4 w-4 shrink-0" />
          Você é Pro! Seus benefícios estão ativos.
        </div>
      )}

      {/* Benefícios */}
      <div className="space-y-3">
        {BENEFITS.map((b) => (
          <Card key={b.title} className="border-brand-100">
            <CardContent className="flex items-start gap-3 pt-4">
              <div className="flex-shrink-0 flex h-9 w-9 items-center justify-center rounded-lg bg-brand-50">
                <b.icon className="h-4 w-4 text-brand-600" />
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-900">{b.title}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{b.description}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Assinatura ativa: mostra card de gerenciamento */}
      {isActive ? (
        <InstructorProCard />
      ) : (
        <Button
          className="w-full bg-brand-600 hover:bg-brand-700 text-white font-semibold h-12"
          onClick={() => createSubscription.mutate()}
          disabled={createSubscription.isPending || subLoading}
          data-testid="subscribe-pro-btn"
        >
          {createSubscription.isPending ? (
            <Loader2 className="h-5 w-5 animate-spin" />
          ) : (
            <>
              <Star className="h-4 w-4 mr-2 fill-white" />
              Assinar Pro por R$49,90/mês
            </>
          )}
        </Button>
      )}

      <p className="text-center text-xs text-muted-foreground">
        Cancele a qualquer momento. Sem multa ou fidelidade.
      </p>
    </div>
  )
}
